import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SIZE_OPTIONS = [25, 50, 100, 200];

export default function Pagination({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  storageKey,
  testIdPrefix = "pager",
}) {
  // Load persisted page size once on mount
  useEffect(() => {
    if (!storageKey || !onPageSizeChange) return;
    const saved = Number(localStorage.getItem(`pagesize:${storageKey}`));
    if (saved && SIZE_OPTIONS.includes(saved) && saved !== pageSize) {
      onPageSizeChange(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total || 0);
  const prev = () => page > 1 && onChange(page - 1);
  const next = () => page < totalPages && onChange(page + 1);

  const changeSize = (n) => {
    if (storageKey) localStorage.setItem(`pagesize:${storageKey}`, String(n));
    onPageSizeChange && onPageSizeChange(n);
  };

  // Always show the size selector; hide prev/next only if single page
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-3 text-sm border-t border-[var(--border)]">
      <div className="flex items-center gap-3 text-[var(--text-muted)]">
        {total > 0 && <span>{from}–{to} von <b className="text-[var(--text)]">{total}</b></span>}
        {onPageSizeChange && (
          <div className="inline-flex items-center gap-1 text-xs">
            <span className="opacity-70">Pro Seite:</span>
            <select
              value={pageSize}
              onChange={(e) => changeSize(Number(e.target.value))}
              data-testid={`${testIdPrefix}-size`}
              className="h-7 rounded-lg border border-[var(--border)] bg-white px-2 font-mono"
            >
              {SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={prev}
            disabled={page <= 1}
            data-testid={`${testIdPrefix}-prev`}
            className="h-8 w-8 rounded-lg border border-[var(--border)] flex items-center justify-center disabled:opacity-40 hover:bg-[var(--pop-cream)]"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="px-3 font-mono text-xs">
            Seite <b>{page}</b> / {totalPages}
          </span>
          <button
            onClick={next}
            disabled={page >= totalPages}
            data-testid={`${testIdPrefix}-next`}
            className="h-8 w-8 rounded-lg border border-[var(--border)] flex items-center justify-center disabled:opacity-40 hover:bg-[var(--pop-cream)]"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
